// Admin API client — /admin/crm/* (mirrors the API's crm aggregate).

import type {
  CrmAccountCreateInput,
  CrmAccountDetailResponse,
  CrmAccountListResponse,
  CrmAccountMutationResponse,
  CrmAccountRenameResponse,
  CrmCloseInput,
  CrmContactResponse,
  CrmContactUpsertInput,
  CrmDeleteResponse,
  CrmEventAppendInput,
  CrmEventResponse,
  CrmLastTouchInput,
  CrmRosterAddInput,
  CrmRosterCloseInput,
  CrmRosterItemResponse,
  CrmRosterListResponse,
  CrmPriority,
  CrmSegment,
  CrmParkedEntry,
  CrmParkedResponse,
  CrmSlotsReplaceInput,
  CrmSlotsResponse,
  CrmStageSetInput,
  CrmSummaryResponse,
  CrmWeekPlanResponse,
  CrmWeekPlanWriteInput,
} from '@a7y/types';
import { client } from './transport';

export const crmApi = {
  // CRM scoreboard (A7Y-4981): `/admin/crm/*` on a7y-api. 409 CRM_UNAVAILABLE
  // outside production; each read carries `imported`.
  getCrmSummary: () => client.get<CrmSummaryResponse>('/crm/summary'),
  getCrmAccounts: () => client.get<CrmAccountListResponse>('/crm/accounts'),
  getCrmSlots: () => client.get<CrmSlotsResponse>('/crm/slots'),
  // CRM account page, editing and this-week view (A7Y-4982/4983/4984). Every
  // write goes through the same `/admin/crm` route the Claude connector uses;
  // the API stamps the acting admin. Rename and the deletes are super_admin only.
  getCrmAccount: (accountId: string) =>
    client.get<CrmAccountDetailResponse>(`/crm/accounts/${encodeURIComponent(accountId)}`),
  getCrmRosterItems: (status: 'open' | 'closed') =>
    client.get<CrmRosterListResponse>(`/crm/roster-items?status=${status}`),
  getCrmWeekPlan: (weekStart: string) =>
    client.get<CrmWeekPlanResponse>(`/crm/week-plans/${encodeURIComponent(weekStart)}`),
  createCrmAccount: (input: CrmAccountCreateInput) =>
    client.post<CrmAccountMutationResponse>('/crm/accounts', input),
  setCrmStage: (accountId: string, input: CrmStageSetInput) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/stage`,
      input
    ),
  closeCrmAccount: (accountId: string, input: CrmCloseInput) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/close`,
      input
    ),
  setCrmLastTouch: (accountId: string, input: CrmLastTouchInput) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/last-touch`,
      input
    ),
  setCrmNextAction: (accountId: string, nextAction: string | null) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/next-action`,
      { nextAction }
    ),
  setCrmSegment: (accountId: string, segment: CrmSegment) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/segment`,
      {
        segment,
      }
    ),
  /** A7Y-5203: set (or clear with null) the account's priority rank. */
  setCrmPriority: (accountId: string, priority: CrmPriority | null) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/priority`,
      {
        priority,
      }
    ),
  setCrmWatch: (accountId: string, watch: string | null) =>
    client.post<CrmAccountMutationResponse>(
      `/crm/accounts/${encodeURIComponent(accountId)}/watch`,
      {
        watch,
      }
    ),
  renameCrmAccount: (accountId: string, newId: string) =>
    client.post<CrmAccountRenameResponse>(`/crm/accounts/${encodeURIComponent(accountId)}/rename`, {
      newId,
    }),
  deleteCrmAccount: (accountId: string) =>
    client.delete<CrmDeleteResponse>(`/crm/accounts/${encodeURIComponent(accountId)}`),
  upsertCrmContact: (input: CrmContactUpsertInput) =>
    client.post<CrmContactResponse>('/crm/contacts', input),
  setCrmContactDeparted: (contactId: string, departed: boolean) =>
    client.post<CrmContactResponse>(`/crm/contacts/${encodeURIComponent(contactId)}/departed`, {
      departed,
    }),
  setCrmContactPrimary: (contactId: string) =>
    client.post<CrmContactResponse>(`/crm/contacts/${encodeURIComponent(contactId)}/primary`, {}),
  setCrmContactReportsTo: (contactId: string, reportsTo: string | null) =>
    client.post<CrmContactResponse>(`/crm/contacts/${encodeURIComponent(contactId)}/reports-to`, {
      reportsTo,
    }),
  deleteCrmContact: (contactId: string) =>
    client.delete<CrmDeleteResponse>(`/crm/contacts/${encodeURIComponent(contactId)}`),
  appendCrmEvent: (input: CrmEventAppendInput) =>
    client.post<CrmEventResponse>('/crm/events', input),
  deleteCrmEvent: (eventId: string) =>
    client.delete<CrmDeleteResponse>(`/crm/events/${encodeURIComponent(eventId)}`),
  addCrmRosterItem: (input: CrmRosterAddInput) =>
    client.post<CrmRosterItemResponse>('/crm/roster-items', input),
  redateCrmRosterItem: (itemId: string, due: string) =>
    client.post<CrmRosterItemResponse>(`/crm/roster-items/${encodeURIComponent(itemId)}/redate`, {
      due,
    }),
  closeCrmRosterItem: (itemId: string, input: CrmRosterCloseInput) =>
    client.post<CrmRosterItemResponse>(
      `/crm/roster-items/${encodeURIComponent(itemId)}/close`,
      input
    ),
  linkCrmRosterItem: (itemId: string, accountId: string) =>
    client.post<CrmRosterItemResponse>(`/crm/roster-items/${encodeURIComponent(itemId)}/link`, {
      accountId,
    }),
  deleteCrmRosterItem: (itemId: string) =>
    client.delete<CrmDeleteResponse>(`/crm/roster-items/${encodeURIComponent(itemId)}`),
  /** A7Y-5210: slots, parked and the week plan are replaced whole, as the MCP's `replace_slots`, `replace_parked` and `write_week_plan` do. */
  replaceCrmSlots: (slots: CrmSlotsReplaceInput['slots']) =>
    client.put<CrmSlotsResponse>('/crm/slots', { slots }),
  replaceCrmParked: (entries: CrmParkedEntry[]) =>
    client.put<CrmParkedResponse>('/crm/parked', { entries }),
  writeCrmWeekPlan: (weekStart: string, input: CrmWeekPlanWriteInput) =>
    client.put<CrmWeekPlanResponse>(`/crm/week-plans/${encodeURIComponent(weekStart)}`, input),
};
